import { formatRMVWithIcon } from 'routes/singleEvent/utils/formatRMVWithIcon';
import { BasicButton } from 'routes/staking_v2/components/BasicButton';

import { DailyRewardsModal } from '../DailyRewardsModal';

/**
 * The prize kinds a VIP multiplier actually reaches.
 *
 * `_apply_prize_multiplier` (`daily_login/views.py`) scales exactly two fields --
 * `token_amount` and `per_person_count` -- and it is only ever called on the
 * AUTO-DISTRIBUTED path. `MANUALLY_DISTRIBUTED_KINDS` (`events/reward_distribution.py`)
 * is `{mystery, nft, lootbox, package}`, and for those the claim view returns the
 * serialised reward BEFORE reaching that call: the multiplier is never applied at all.
 *
 * So promising one on those kinds promises something that never lands -- and worse,
 * unrecoverably: `DailyLoginClaim` stores no multiplier, and the value is recomputed
 * from the holder's LIVE VIP level on every read, so if their level moves before a human
 * distributes the prize, nobody can reconstruct what the modal said at claim time.
 *
 * Nothing on the backend stops an admin attaching a multiplier range to an NFT or package
 * day -- `vip_multipliers` is a plain M2M with no kind restriction -- so this gate cannot
 * be replaced by an assumption about how the calendar is configured.
 */
const MULTIPLIED_PRIZE_KINDS = new Set(['token', 'vip_points', 'wof_spins', 'vip_wof_spins', 'attempts']);

type YouWonModalProps = {
  onClose: () => void;
  onClaimReward: () => void;
  rewardName?: string;
  rewardIconUrl?: string;
  vipMultiplier?: string;
  /**
   * The prize's `kind` (`events.PrizeKind`). Only `'token'` prizes are paid in RMV and
   * therefore land in royalties — every other kind (VIP points, NFTs, spins, lootboxes,
   * packages) credits somewhere else and must not be told it will appear tomorrow.
   *
   * Gated on the kind rather than on `token_currency` deliberately: that field is a currency
   * ID on the wire but is typed `string | null` in `dailyRewards.models.ts`, and one route
   * already resolves it through a hardcoded `{1: 'RMV'}` map. A user-facing promise about
   * where money lands should not rest on a magic number two type definitions disagree about.
   * If a non-RMV token prize is ever added here, this is the line to revisit.
   */
  prizeKind?: string;
};

export const YouWonModal = ({
  onClose,
  onClaimReward,
  rewardName,
  rewardIconUrl,
  vipMultiplier,
  prizeKind,
}: YouWonModalProps) => {
  const displayName = rewardName ?? 'Reward';
  const formattedAmount = rewardIconUrl ? (
    <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-[8px]">
      <img src={rewardIconUrl} alt="" className="tw-h-[48px] tw-w-[48px] tw-object-contain" />
      {displayName}
    </span>
  ) : (
    formatRMVWithIcon(displayName, '!tw-h-[48px] !tw-w-[48px]')
  );

  return (
    <DailyRewardsModal
      onClose={onClose}
      title="Congratulations!"
      className="tw-w-[400px] lg:tw-w-[400px]"
      titleClassName="tw-text-[32px] tw-font-medium lg:!tw-text-[32px]"
    >
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[10px] tw-p-[20px] tw-text-white">
        <span className="tw-text-[20px]">You&apos;ve won</span>
        {/*
          BELOW the amount, tight against it.

          It used to be a 10px pill absolutely positioned at `-top-6px -right-20px`, which
          could hold "2x" and nothing longer and overlapped a 40px figure that grows with the
          prize name. Its own line cannot collide; the 2px wrapper gap rather than the
          column's 10px is what keeps it reading as part of the figure instead of as the next
          thing down.

          "APPLIES", present tense about a rule, because the figure above it is the prize
          BEFORE the multiplier: `daily_login/views.py:315-318` serialises the raw
          `reward.prize` and the multiplier reaches this modal only as metadata, then gets
          applied separately at `views.py:330` to the copy handed to `distribute_prize`. A
          holder shown 50 receives 100. Past tense would understate the reward — the
          opposite of the royalty card's "x2.5 applied" badge, which sits beside a figure the
          rate is already in.

          Shown only above 1x -- a "1x multiplier" credits a VIP level with something it did
          not do -- and only for the kinds the multiplier actually reaches, which is what
          MULTIPLIED_PRIZE_KINDS is for.
        */}
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[2px]">
          <span className="tw-text-[40px] tw-font-bold">{formattedAmount}</span>
          {vipMultiplier && Number(vipMultiplier) > 1 && MULTIPLIED_PRIZE_KINDS.has(prizeKind ?? '') && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-px-[8px] tw-py-[3px] tw-text-[11px] tw-font-bold tw-leading-none tw-text-[#0e2659]">
              {String(Number(vipMultiplier))}x VIP multiplier applies
            </span>
          )}
        </div>
        {/*
          RMV only. `kind === "token"` is the one prize kind paid in RMV, and RMV is what goes
          to royalties — VIP points, NFTs, spins, lootboxes and packages all credit elsewhere
          and would be told, wrongly, to wait until tomorrow for something already theirs.
        */}
        {prizeKind === 'token' && (
          <p className="tw-text-[10px]">
            RMV rewards are credited to royalties automatically and will be visible the next day, dated today.
          </p>
        )}
        <BasicButton
          text="Claim Reward"
          onClick={onClaimReward}
          className="tw-mt-[26px] tw-h-[36px] tw-w-[118px] tw-px-[0px]"
          textClassName="tw-text-[14px]"
        />
      </div>
    </DailyRewardsModal>
  );
};
